import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../lib/store';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Maximize2, Search, X } from 'lucide-react';
import { Input } from '../components/ui/input';

/**
 * Public route `/qr/:id` — when a printed QR sticker is scanned, the phone
 * browser opens this page. Requires HEDA login (no random customer access).
 * The image (or PDF) is fetched as an auth-gated blob so it can't be hot-linked.
 *
 * - Single-page images: shown centered on screen (user request iter82).
 * - PDFs: rendered server-side page-by-page as JPEGs (`/api/qr-codes/{id}/page/{n}`)
 *   and stacked vertically. Mobile browsers cannot reliably render `application/pdf`
 *   blob URLs inside an <iframe> — the iframe just shows blank — so we render the
 *   pages as images instead. The original PDF is still available via the download
 *   button in the header.
 */
export default function QrViewPage() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);          // original (for download)
  const [pdfPageUrls, setPdfPageUrls] = useState([]);    // per-page blob URLs (PDF only)
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // For tap-to-zoom on laptop / desktop (mobile already gets pinch-zoom for free
  // via the permissive viewport meta tag set below). Maps page index → bool.
  const [zoomedPage, setZoomedPage] = useState(null);   // null | index
  // PDF text search — `searchQuery` is what the user typed, `searchHits` is
  // the per-page hit list returned by the backend (normalized bboxes 0..1).
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHits, setSearchHits] = useState(null);   // null | { pages: [{page, count, rects}], total_matches }
  const [searchBusy, setSearchBusy] = useState(false);

  // Auth gate — if not logged in, send to login with redirect back here.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const redirect = encodeURIComponent(`/qr/${id}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [user, authLoading, id, navigate]);

  useEffect(() => {
    if (!user || !id) return;
    const revokeList = [];
    let cancelled = false;
    (async () => {
      try {
        const m = await api.getQrCode(id);
        if (cancelled) return;
        setMeta(m.data);
        const isPdf = (m.data?.content_type || '').toLowerCase() === 'application/pdf';

        if (isPdf) {
          // Don't block the initial paint on the full-PDF
          // download. Kick it off without awaiting so the Download
          // button gains an href a moment later, but page 1 renders as
          // soon as it arrives. Non-PDF entries still await below
          // because the image itself IS what we display.
          api.getQrImageBlob(id).then((orig) => {
            if (cancelled) return;
            const origUrl = URL.createObjectURL(orig.data);
            revokeList.push(origUrl);
            setBlobUrl(origUrl);
          }).catch(() => { /* download button just won't appear — non-fatal */ });

          const info = await api.getQrPdfInfo(id);
          if (cancelled) return;
          const pages = Math.max(1, info.data?.page_count || 1);

          // Fetch page 1 first so the user sees content immediately,
          // then load the rest sequentially and append as each arrives.
          const first = await api.getQrPdfPageBlob(id, 1);
          if (cancelled) return;
          const firstUrl = URL.createObjectURL(first.data);
          revokeList.push(firstUrl);
          setPdfPageUrls([firstUrl]);
          setLoading(false);

          for (let i = 2; i <= pages; i++) {
            if (cancelled) return;
            try {
              const res = await api.getQrPdfPageBlob(id, i);
              if (cancelled) return;
              const url = URL.createObjectURL(res.data);
              revokeList.push(url);
              setPdfPageUrls(prev => [...prev, url]);
            } catch {
              // Stop the loop but keep pages already on screen; the
              // user isn't kicked to the error page — they still have
              // everything up to i-1 available to scroll.
              break;
            }
          }
        } else {
          // Non-PDF: image displayed directly, so we must have the bytes.
          const orig = await api.getQrImageBlob(id);
          if (cancelled) return;
          const origUrl = URL.createObjectURL(orig.data);
          revokeList.push(origUrl);
          setBlobUrl(origUrl);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'QR code not found');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      revokeList.forEach(URL.revokeObjectURL);
    };
  }, [id, user]);

  // Debounced PDF text search — runs ~350ms after the user stops typing.
  useEffect(() => {
    if (!id || !meta) return;
    const isPdf = (meta?.content_type || '').toLowerCase() === 'application/pdf';
    if (!isPdf) return;
    if (!searchQuery.trim()) { setSearchHits(null); return; }
    let cancelled = false;
    setSearchBusy(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.searchQrPdf(id, searchQuery.trim());
        if (!cancelled) setSearchHits(res.data);
      } catch (e) {
        if (!cancelled) setSearchHits({ pages: [], total_matches: 0, error: true });
      } finally {
        if (!cancelled) setSearchBusy(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery, id, meta]);

  /** Jump-scroll to a given (1-indexed) PDF page. */
  const jumpToPage = (pageNum) => {
    const el = document.querySelector(`[data-testid="qr-view-pdf-page-${pageNum}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Make sure mobile pinch-zoom is actually enabled (the default index.html
  // viewport often has user-scalable=no which blocks pinching). Inject the
  // permissive viewport just for this page.
  useEffect(() => {
    const meta = document.querySelector('meta[name=viewport]');
    const original = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes'
      );
    }
    return () => {
      if (meta && original) meta.setAttribute('content', original);
    };
  }, []);

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-heda-mute">Checking access…</div>;
  }

  const isPdf = (meta?.content_type || '').toLowerCase() === 'application/pdf';
  const downloadExt = isPdf ? 'pdf' : (meta?.content_type || 'image/jpeg').split('/')[1];
  // Lookup: page number → array of normalized rects for fast render below.
  const hitRectsByPage = (() => {
    const m = new Map();
    for (const p of (searchHits?.pages || [])) m.set(p.page, p.rects || []);
    return m;
  })();

  return (
    <div className="min-h-screen bg-heda-ink text-white flex flex-col" data-testid="qr-view-page">
      <header className="flex items-center justify-between px-3 py-2 bg-heda-ink border-b border-heda-ink shrink-0 gap-2">
        {/* Back button destination changed from `/` (home)
            to `/qr` (Price List index). Two callers land here:
            (1) QrCodesPage clicks that open target="_blank" — no
                in-app history → native back would exit to about:blank.
            (2) External scanned physical QR stickers — same story.
            In both cases `/qr` is the most useful nearby landing
            page (list of all price lists) and matches the user's
            "back should return to Price List" expectation. */}
        <Button variant="ghost" size="sm" onClick={() => navigate('/qr-codes')} className="rounded-xl text-white hover:bg-heda-ink shrink-0">
          <ArrowLeft className="w-4 h-4 mr-1" /> HEDA
        </Button>
        <div className="text-sm font-medium truncate px-2 flex-1 text-center min-w-0" data-testid="qr-view-label">
          {meta?.label || (loading ? 'Loading…' : '')}
        </div>
        {blobUrl && (
          <a href={blobUrl} download={`${meta?.label || id}.${downloadExt}`}>
            <Button variant="ghost" size="sm" className="rounded-xl text-white hover:bg-heda-ink shrink-0">
              <Download className="w-4 h-4" />
            </Button>
          </a>
        )}
      </header>

      {/* PDF search bar — only shown for PDFs, sticky under the header */}
      {isPdf && (
        <div className="bg-heda-ink/95 backdrop-blur border-b border-heda-ink px-3 py-2 sticky top-0 z-20 shrink-0">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute pointer-events-none" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search text in this PDF…"
              className="rounded-xl pl-9 pr-9 bg-heda-ink border-heda-ink text-white placeholder:text-heda-mute focus-visible:ring-heda-gold"
              data-testid="qr-view-pdf-search-input"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-heda-mute hover:text-white"
                aria-label="Clear search"
                data-testid="qr-view-pdf-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery.trim() && (
            <div className="max-w-2xl mx-auto mt-2 text-xs text-heda-mute flex flex-wrap items-center gap-2" data-testid="qr-view-pdf-search-results">
              {searchBusy ? (
                <span>Searching…</span>
              ) : searchHits?.error ? (
                <span className="text-red-700">Search failed</span>
              ) : (searchHits?.total_matches ?? 0) === 0 ? (
                <span>No matches found</span>
              ) : (
                <>
                  <span className="font-semibold text-heda-gold">{searchHits.total_matches} matches</span>
                  {searchHits.pages.map((p) => (
                    <button
                      key={p.page}
                      type="button"
                      onClick={() => jumpToPage(p.page)}
                      className="px-2 py-0.5 rounded-lg bg-heda-ink border border-heda-ink hover:border-heda-gold hover:text-white"
                      data-testid={`qr-view-pdf-search-jump-${p.page}`}
                    >
                      Page {p.page} ({p.count})
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* PDFs scroll from the top; images sit centered on screen */}
      <main
        className={
          'flex-1 overflow-auto flex justify-center ' +
          (isPdf ? 'items-start' : 'items-center')
        }
      >
        {loading && <p className="text-heda-mute mt-12">Loading…</p>}
        {error && (
          <div className="mt-12 text-center px-4">
            <p className="text-red-700 mb-2">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-xl border-white text-white">Back to HEDA</Button>
          </div>
        )}
        {!error && !loading && isPdf && pdfPageUrls.length > 0 && (
          <div
            className="w-full flex flex-col items-center gap-2 p-2"
            style={{ touchAction: 'pan-x pan-y pinch-zoom' }}
            data-testid="qr-view-pdf-pages"
          >
            {pdfPageUrls.map((url, i) => {
              const isZoomed = zoomedPage === i;
              const pageNum = i + 1;
              const rects = hitRectsByPage.get(pageNum) || [];
              return (
                <div key={i} className="relative w-full flex justify-center">
                  <div className="relative inline-block max-w-full">
                    <img
                      src={url}
                      alt={`Page ${pageNum}`}
                      onClick={() => setZoomedPage(z => (z === i ? null : i))}
                      className={
                        'h-auto bg-white shadow-md select-none cursor-zoom-in transition-transform origin-top ' +
                        (isZoomed ? 'max-w-[200%] cursor-zoom-out scale-[1.6] sm:scale-[2]' : 'max-w-full block')
                      }
                      style={{ touchAction: 'pan-x pan-y pinch-zoom' }}
                      loading={pageNum === 1 ? 'eager' : 'lazy'}
                      data-testid={`qr-view-pdf-page-${pageNum}`}
                    />
                    {/* Highlight rectangles from PDF search — placed in % so
                        they scale perfectly with the image even when zoomed. */}
                    {rects.map((r, k) => (
                      <div
                        key={k}
                        className="pointer-events-none absolute bg-amber-200/55 border border-amber-700/80 rounded-[1px]"
                        style={{
                          left: `${r.x * 100}%`,
                          top: `${r.y * 100}%`,
                          width: `${r.w * 100}%`,
                          height: `${r.h * 100}%`,
                        }}
                        data-testid={`qr-view-pdf-highlight-${pageNum}-${k}`}
                      />
                    ))}
                  </div>
                  {/* Desktop hint icon — fades out when zoomed */}
                  <button
                    type="button"
                    onClick={() => setZoomedPage(z => (z === i ? null : i))}
                    aria-label={isZoomed ? 'Reset zoom' : 'Zoom in'}
                    className={
                      'absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 backdrop-blur-sm transition-opacity ' +
                      (isZoomed ? 'opacity-100' : 'opacity-60 hover:opacity-100')
                    }
                    data-testid={`qr-view-pdf-zoom-${pageNum}`}
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {!error && !loading && !isPdf && blobUrl && (
          // Single image — centered on screen (vertically + horizontally).
          // When zoomed, pinch-pan still works because `touchAction: pan-x pan-y pinch-zoom`
          // is on a scrollable parent AND the child image, so a finger
          // resting on the image can still drive vertical AND horizontal
          // scroll (the child's touch-action was overriding the parent's,
          // blocking one-finger scroll on top of the page).
          <div
            className="w-full h-full flex items-center justify-center p-2 overflow-auto"
            style={{ touchAction: 'pan-x pan-y pinch-zoom' }}
          >
            <img
              src={blobUrl}
              alt={meta?.label || 'Price list'}
              className="max-w-full max-h-full h-auto w-auto select-none"
              style={{ touchAction: 'pan-x pan-y pinch-zoom' }}
              data-testid="qr-view-image"
            />
          </div>
        )}
      </main>
    </div>
  );
}
